import {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Editor } from '@tiptap/react';
import { funnel } from 'remeda';

interface EditorBubbleMenuContextType {
  editor: Editor | null;
  bubbleViewState: string | boolean;
  setBubbleView: (value: string | boolean) => void;
  bubbleCleanup: () => void;
  removeAnchor: (linkHref: string) => void;
}

const EditorBubbleMenuContext = createContext<EditorBubbleMenuContextType | undefined>(
  undefined,
);

export const useEditorBubbleMenu = () => {
  const context = useContext(EditorBubbleMenuContext);
  if (!context) {
    throw new Error(
      'useEditorBubbleMenu must be used within an EditorBubbleMenuContextProvider',
    );
  }
  return context;
};

interface EditorBubbleMenuProviderProps extends PropsWithChildren {
  editor: Editor | null;
}

export const EditorBubbleMenuProvider = ({
  editor,
  children,
}: EditorBubbleMenuProviderProps) => {
  const selectionRef = useRef<{ from: number; to: number } | null>(null);
  const [bubbleViewState, setBubbleViewState] = useState<boolean | string>(false);

  // Function to update the bubble view
  const setBubbleView = useCallback(
    (value: string | boolean) => {
      if (!editor || editor.isDestroyed) {
        return;
      }

      editor.storage.bubbleMenu = { view: value };
      setBubbleViewState(value);

      // Force the editor to re-evaluate bubble menu state
      if (value === 'linkEditor' || value === 'threadsEditor') {
        editor.view.dispatch(editor.state.tr);
      }
    },
    [editor],
  );

  const removeAnchor = useCallback(
    (linkHref: string) => {
      if (!editor || editor.isDestroyed) {
        return;
      }

      if (linkHref && linkHref.startsWith('#')) {
        const anchorId = linkHref.substring(1);

        const element = document.getElementById(anchorId);

        if (element) {
          // Find the node in the editor content that corresponds to this element
          // and remove its ID attribute
          const transaction = editor.state.tr;

          // Find the position of the node with this ID in the document
          editor.state.doc.descendants((node, pos) => {
            if (node.attrs && node.attrs.id === anchorId) {
              // Create a new set of attributes without the ID
              const newAttrs = { ...node.attrs };
              delete newAttrs.id;

              // Update the node with new attributes (without ID)
              transaction.setNodeMarkup(pos, null, newAttrs);

              return false; // Stop traversing once found
            }
            return true;
          });

          // Apply the transaction
          editor.view.dispatch(transaction);
        } else {
          console.warn(
            `[EditorBubbleMenuProvider] - removeAnchor: No DOM element with ID "${anchorId}" found`,
          );
        }
      }
    },
    [editor],
  );

  const bubbleCleanup = useCallback(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    // If link or table is active, do not cleanup
    if (editor.isActive('link') || editor.isActive('table')) {
      return;
    }

    // If text is selected, set to bubbleMenu instead of false
    if (!editor.state.selection.empty) {
      editor.storage.bubbleMenu = { view: 'bubbleMenu' };
      setBubbleView('bubbleMenu');
    } else {
      // No selection - hide completely
      editor.storage.bubbleMenu = { view: false };
      setBubbleView(false);
    }

    selectionRef.current = null;
  }, [editor, setBubbleView, selectionRef]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    // Capture the last known snapshot.
    let lastSnapshot = editor.storage?.bubbleMenu?.view || false;

    // https://remedajs.com/docs/#funnel - similar to debounce
    const handleChangeFunnel = funnel(
      () => {
        const newSnapshot = editor.storage?.bubbleMenu?.view || false;
        if (newSnapshot !== lastSnapshot) {
          lastSnapshot = newSnapshot;
          setBubbleViewState(newSnapshot);
        }
      },
      { minQuietPeriodMs: 100 },
    );

    editor.on('transaction', handleChangeFunnel.call);
    editor.on('blur', () => {
      // Check after the next repaint if we're transitioning to a special menu view
      requestAnimationFrame(() => {
        const currentView = editor.storage?.bubbleMenu?.view;
        // Don't cleanup if we've transitioned to linkEditor, threadsEditor, etc.
        if (
          currentView &&
          typeof currentView === 'string' &&
          currentView !== 'bubbleMenu'
        ) {
          return;
        }
        bubbleCleanup();
      });
    });
    return () => {
      editor.off('transaction', handleChangeFunnel.call);
      if (typeof handleChangeFunnel.flush === 'function') {
        handleChangeFunnel.flush();
      }
      editor.off('blur', () => {
        bubbleCleanup();
      });
    };
  }, [editor, bubbleCleanup]);

  // When the bubble menu opens, store the current selection and apply highlighting.
  useEffect(() => {
    if (!editor || editor.isDestroyed || !bubbleViewState) return;

    if (!editor.state.selection.empty && selectionRef.current === null) {
      const { from, to } = editor.state.selection;
      selectionRef.current = { from, to };
    }
  }, [editor, bubbleViewState]);

  const value = useMemo(
    () => ({
      editor,
      bubbleViewState,
      setBubbleView,
      bubbleCleanup,
      removeAnchor,
    }),
    [editor, bubbleViewState, setBubbleView, bubbleCleanup, removeAnchor],
  );

  return (
    <EditorBubbleMenuContext.Provider value={value}>
      {children}
    </EditorBubbleMenuContext.Provider>
  );
};

export default EditorBubbleMenuProvider;

/**
 * As an alternative to useEffect and useState combo if we would like
 * to just use the editors internal storage as our state we can
 * use this react hook - useSyncExternalStore.
 * // https://react.dev/reference/react/useSyncExternalStore
 * // useSyncExternalStore subscribes to the editor storage value
 *
 * const bubbleView = useSyncExternalStore(
 *  (callback) => {
 *    if (!editor) return () => {}; // If theres no editor, return a no op.
 *    // Capture last known snapshot
 *    let lastSnapshot = editor.storage?.bubbleMenu?.view || false;
 *    // Remeda funnel (similar to a debounce)
 *    const handleChangeFunnel = funnel(
 *      () => {
 *        const newSnapshot = editor.storage?.bubbleMenu?.view || false;
 *        if (newSnapshot !== lastSnapshot) {
 *          lastSnapshot = newSnapshot; // Update snapshot
 *          callback(); // Trigger sync callback to rerender on snapshot change
 *        }
 *      },
 *      { minQuietPeriodMs: 100 },
 *    );
 *    // Register debounce funnel for transactions
 *    editor.on('transaction', handleChangeFunnel.call);
 *    return () => {
 *      editor.off('transaction', handleChangeFunnel.call);
 *      if (typeof handleChangeFunnel.flush === 'function') {
 *        handleChangeFunnel.flush(); // Flush funnel on unmount
 *      }
 *    };
 *  },
 *  () => editor?.storage?.bubbleMenu?.view || false,
 *);
 */
